<template>
  <!-- One card per feature area, each opening its section on /features. Shown on the home page and
       both pricing pages, so every page names the same features. Clicks are tracked with the page
       area they came from (`location`). -->
  <v-row>
    <v-col v-for="area in visibleAreas" :key="area.key" cols="12" sm="6" md="4" class="d-flex">
      <router-link
        :to="areaUrl(area.key)"
        class="as-home-feature-card"
        @click.native="onAreaClick(area.key)"
      >
        <!-- Icon and title on one line: on a phone an icon on its own line costs a screen of height. -->
        <div class="as-home-feature-card__top">
          <v-icon color="var(--color-pallet-1)" class="flex-shrink-0">{{ area.icon }}</v-icon>
          <h3 class="as-home-feature-card__title">{{ $t(`pages.features.areas.${area.key}.title`) }}</h3>
        </div>
        <div v-if="area.optional || areaNotes[area.key]" class="as-home-feature-card__badges">
          <span v-if="area.optional" class="as-home-feature-card__optional">{{ $t('pages.features.optional') }}</span>
          <span v-if="areaNotes[area.key]" class="as-home-feature-card__optional as-home-feature-card__note">{{ areaNotes[area.key] }}</span>
        </div>
        <!-- One line each; the whole card opens its section (no "Uzzināt vairāk" per card, §13.2). -->
        <p class="as-home-feature-card__summary">{{ $t(`pages.features.areas.${area.key}.summary`) }}</p>
      </router-link>
    </v-col>
  </v-row>
</template>

<script>
import urlUtil from '@/js/utils/urlUtil'
import { MainPath } from '@/router/mainPaths'
import { FEATURE_AREAS } from '@/domains/website/featureCatalogue'
import { trackWebsiteEvent, WebsiteEvent } from '@/domains/website/websiteTracking'

/** Cards shown on a phone; the rest are on /features. */
const PHONE_VISIBLE_AREAS = 3

export default {
  name: 'FeatureAreaCards',
  props: {
    /** WebsiteLocation of the page area, reported with each click. */
    location: { type: String, required: true },
    /** FeatureAreaKey values not to show, e.g. studio features on the single-trainer pricing page. */
    excludeKeys: { type: Array, default: () => [] },
    /** A short note per FeatureAreaKey, shown as a badge (e.g. "No Active plāna" on the pricing page). */
    areaNotes: { type: Object, default: () => ({}) },
    /**
     * On a phone, show only the first few cards: a long stack hides the rest of the page. The
     * parent's link to /features is the way to all of them (Ansis, 2026-10-26).
     */
    collapseOnPhone: { type: Boolean, default: false }
  },
  computed: {
    areas () {
      return FEATURE_AREAS.filter(area => !this.excludeKeys.includes(area.key))
    },
    isCollapsed () {
      return this.collapseOnPhone && this.$vuetify.breakpoint.smAndDown && this.areas.length > PHONE_VISIBLE_AREAS
    },
    visibleAreas () {
      return this.isCollapsed ? this.areas.slice(0, PHONE_VISIBLE_AREAS) : this.areas
    }
  },
  methods: {
    areaUrl (key) {
      return `${urlUtil.getLocalisedUrl('/' + MainPath.FEATURES)}#${key}`
    },
    onAreaClick (key) {
      trackWebsiteEvent(this.$gtag, WebsiteEvent.FEATURE_CLICK, { feature: key, location: this.location })
    }
  }
}
</script>

<style>
.as-home-feature-card {
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  background: var(--color-white);
  border-radius: 16px;
  padding: 24px;
  border: 2px solid transparent;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  text-decoration: none;
  color: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.as-home-feature-card:hover,
.as-home-feature-card:focus-visible {
  border-color: var(--color-pallet-1);
  box-shadow: 0 8px 28px rgba(46, 139, 87, 0.15);
}

.as-home-feature-card__top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.as-home-feature-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.as-home-feature-card__note {
  border-color: var(--color-pallet-4) !important;
}

.as-home-feature-card__optional {
  align-self: flex-start;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-pallet-2);
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 999px;
  padding: 2px 10px;
}

.as-home-feature-card__title {
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--color-pallet-5);
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
}

.as-home-feature-card__summary {
  color: var(--color-pallet-2);
  line-height: 1.55;
  margin-bottom: 16px;
  overflow-wrap: anywhere;
}

@media (max-width: 600px) {
  .as-home-feature-card {
    padding: 18px;
  }

  .as-home-feature-card__summary {
    margin-bottom: 10px;
  }
}

.as-home-feature-card__more {
  margin-top: auto;
  color: var(--color-pallet-1);
  font-weight: 600;
}
</style>
